import { Component } from '@theme/component';
import { applyPropertiesToPayload, resolveProperties } from '@theme/cart-attribution';

class CartBestsellersComponent extends Component {
  /** @type {Element | null} */
  #drawer = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pollTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #debounceTimer;

  /**
   * Whether we've already asked Rebuy to re-scan the DOM during the current
   * polling chain. Reset on every fetch trigger so each drawer open / cart
   * update gets one fresh re-scan.
   * @type {boolean}
   */
  #didRebuyInit = false;

  /** @type {Set<number>} */
  #cartProductIds = new Set();

  /**
   * Every server-rendered collection slide, in collection order — including the
   * ones currently detached from the carousel because their product is in the
   * cart or because they overflow `max_products`.
   * @type {HTMLElement[]}
   */
  #collectionSlides = [];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.#seedCartProductIds();
    document.addEventListener('cart:update', this.#onCartUpdate);

    if (this.dataset.mode === 'rebuy') {
      this.#initRebuyMode();
    } else {
      this.#seedCollectionSlides();
      // Swiper initialises from a `requestIdleCallback`, so the wrapper is
      // trimmed to the visible set before Swiper ever reads the slides — no
      // reinit needed on this first pass.
      this.#renderCollectionSlides(false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    clearTimeout(this.#pollTimer);
    clearTimeout(this.#debounceTimer);

    if (this.#drawer) {
      this.#drawer.removeEventListener('dialog:open', this.#onRebuyTrigger);
    }
    document.removeEventListener('cart:update', this.#onRebuyTrigger);
    document.removeEventListener('cart:update', this.#onCartUpdate);
  }

  /* ─── Cart product ID tracking (shared by both modes) ───────────── */

  /**
   * Seed `#cartProductIds` from the server-rendered `data-cart-product-ids`
   * attribute so the initial render is accurate without a network request.
   */
  #seedCartProductIds() {
    const attr = this.dataset.cartProductIds;
    if (!attr) {
      this.#cartProductIds = new Set();
      return;
    }
    this.#cartProductIds = new Set(
      attr
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n))
    );
  }

  /**
   * Fired on every `cart:update` document event (both modes). Updates the
   * in-memory set of cart product IDs, then for collection mode immediately
   * rebuilds the carousel from it. For Rebuy mode the set is updated here so
   * that the subsequent debounced `#renderRebuyCards` call reads the latest
   * state.
   */
  #onCartUpdate = (/** @type {CustomEvent} */ event) => {
    const items = event.detail?.resource?.items;
    // A cart:update carrying no cart payload tells us nothing about what is in
    // the cart — keep the last known state rather than reading it as empty.
    if (!Array.isArray(items)) return;

    this.#cartProductIds = new Set(items.map((item) => item.product_id));

    if (this.dataset.mode === 'collection') {
      this.#renderCollectionSlides(true);
    }
    // Rebuy mode: #onRebuyTrigger fires next (registered after #onCartUpdate)
    // and debounces a fresh #renderRebuyCards call that will read the updated Set.
  };

  /* ─── Collection mode ──────────────────────────────── */

  /**
   * Take ownership of the slides the snippet rendered. It renders a pool larger
   * than `max_products`, so which of them are in the carousel at any moment is
   * decided here rather than in Liquid.
   */
  #seedCollectionSlides() {
    const wrapper = this.querySelector('.swiper-wrapper');
    this.#collectionSlides = /** @type {HTMLElement[]} */ (
      wrapper ? Array.from(wrapper.querySelectorAll(':scope > .swiper-slide[data-product-id]')) : []
    );
  }

  /**
   * Rebuild the carousel from the candidate pool: the slides whose product is
   * not in the cart, capped at `max_products`, in collection order. Slides left
   * out are detached but kept in `#collectionSlides`, so removing a product from
   * the cart puts its slide back instead of leaving a permanent gap.
   *
   * @param {boolean} reinit - Rebuild the Swiper instance after the swap.
   */
  #renderCollectionSlides(reinit) {
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const max = parseInt(this.dataset.maxProducts, 10) || 6;
    const visible = this.#collectionSlides
      .filter((slide) => !this.#cartProductIds.has(Number(slide.dataset.productId)))
      .slice(0, max);

    if (visible.length === 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    wrapper.replaceChildren(...visible);

    if (reinit) this.#reinitCarousel();
  }

  /* ─── Carousel (shared by both modes) ──────────────────── */

  /** Rebuild the Swiper instance after the set of slides changed. */
  #reinitCarousel() {
    const carousel = /** @type {any} */ (this.querySelector('carousel-component'));
    if (!carousel) return;

    if (carousel.SwiperClass) {
      carousel.initSwiper();
    } else {
      carousel.loadSwiper().then(() => carousel.initSwiper());
    }
  }

  /* ─── Rebuy mode ─────────────────────────────────────────────────── */

  #initRebuyMode() {
    this.#drawer = this.closest('cart-drawer-component');

    // `dialog:open` does not bubble — listen on the drawer element itself.
    this.#drawer?.addEventListener('dialog:open', this.#onRebuyTrigger);
    document.addEventListener('cart:update', this.#onRebuyTrigger);

    // If the drawer is already open when this connects (e.g. re-attached by a
    // morph while open), fetch immediately.
    if (this.#drawer?.querySelector('dialog[open]')) {
      this.#fetchRebuyProducts();
    }
  }

  /**
   * Debounced trigger for Rebuy data fetching. Called on drawer open and
   * cart:update events.
   */
  #onRebuyTrigger = () => {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#fetchRebuyProducts(), 200);
  };

  #fetchRebuyProducts() {
    this.#didRebuyInit = false;
    clearTimeout(this.#pollTimer);
    this.#pollForRebuyData(0);
  }

  /**
   * Poll `window.Rebuy.widgets` for the configured widget ID and extract
   * product data. Rebuy loads asynchronously, so poll for up to ~6s.
   *
   * @param {number} attempt
   */
  #pollForRebuyData(attempt) {
    const rebuy = /** @type {any} */ (window).Rebuy;
    const widgetId = this.dataset.rebuyWidgetId;

    // Gate: Rebuy's widget API must be fully booted.
    if (!rebuy || typeof rebuy.reinit !== 'function') {
      if (attempt < 40) {
        this.#pollTimer = setTimeout(() => this.#pollForRebuyData(attempt + 1), 150);
      }
      return;
    }

    const widget = Array.isArray(rebuy.widgets)
      ? rebuy.widgets.find((/** @type {any} */ w) => String(w?.id) === String(widgetId))
      : null;

    if (widget?.data?.products?.length > 0) {
      this.#renderRebuyCards(widget.data.products);
      return;
    }

    // Widget not registered yet — call init() once to re-scan the DOM and
    // pick up the hidden data-rebuy-id bridge element.
    if (!this.#didRebuyInit) {
      this.#didRebuyInit = true;
      try {
        rebuy.init();
      } catch {
        // ignore Rebuy init errors — polling will simply time out
      }
    }

    if (attempt < 40) {
      this.#pollTimer = setTimeout(() => this.#pollForRebuyData(attempt + 1), 150);
    }
  }

  /**
   * Build card HTML from Rebuy product data and inject into the carousel.
   * Products already present in the cart are excluded before slicing to `max`.
   *
   * @param {any[]} products
   */
  #renderRebuyCards(products) {
    const max = parseInt(this.dataset.maxProducts, 10) || 6;
    const filtered = products.filter((p) => !this.#cartProductIds.has(p.id));
    const items = filtered.slice(0, max);

    if (items.length === 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    wrapper.innerHTML = items.map((product) => this.#buildCardHtml(product)).join('');

    this.#reinitCarousel();
  }

  /**
   * Build a single card HTML string matching the `.cart-bestsellers__card`
   * structure from the Liquid template.
   *
   * @param {any} product
   * @returns {string}
   */
  #buildCardHtml(product) {
    const variant = product.variants?.[0];
    const handle = product.handle || '';
    const title = this.#escapeHtml(product.title || '');
    const imageSrc = product.image?.src || product.featured_image || '';
    const imageAlt = this.#escapeHtml(product.image?.alt || product.title || '');
    const variantId = variant?.id || '';
    const isAvailable = variant?.available !== false;

    const currentPrice = this.#formatMoney(variant?.price);
    const priceCents = this.#toCents(variant?.price);
    const compareCents = this.#toCents(variant?.compare_at_price);
    // Only render a compare price when it is genuinely higher than the current
    // price. Rebuy returns compare_at_price === price for non-discounted
    // products, which must not show a struck-through duplicate.
    const comparePrice =
      !isNaN(compareCents) && !isNaN(priceCents) && compareCents > priceCents
        ? this.#formatMoney(variant.compare_at_price)
        : '';

    const imageHtml = imageSrc
      ? `<img
          src="${this.#escapeHtml(imageSrc + (imageSrc.includes('?') ? '&' : '?') + 'width=128')}"
          alt="${imageAlt}"
          width="64"
          height="64"
          loading="lazy"
          class="cart-bestsellers__image"
        >`
      : `<div class="cart-bestsellers__placeholder"></div>`;

    const comparePriceHtml = comparePrice
      ? `<s class="cart-bestsellers__compare-price">${comparePrice}</s>`
      : '';

    const ratingHtml = this.#buildRatingHtml(product);

    // Description is intentionally omitted in Rebuy mode: the
    // `anatta.card_short_description` metafield is not present in
    // Rebuy's widget payload, so there is no reliable source for it here.
    return `<div class="swiper-slide cart-bestsellers__slide">
      <div class="cart-bestsellers__card">
        <div class="cart-bestsellers__image-wrapper">
          ${imageHtml}
        </div>
        <div class="cart-bestsellers__info">
          ${ratingHtml}
          <a href="/products/${this.#escapeHtml(handle)}" class="cart-bestsellers__title">
            ${title}
          </a>
          <div class="cart-bestsellers__price-row">
            <div class="cart-bestsellers__prices">
              <span class="cart-bestsellers__current-price">${currentPrice}</span>
              ${comparePriceHtml}
            </div>
            <button
              type="button"
              class="cart-bestsellers__add-btn"
              data-variant-id="${variantId}"
              ${isAvailable ? '' : 'disabled'}
            >
              ADD
            </button>
          </div>
        </div>
      </div>
    </div>`;
  }

  /**
   * Renders a Bazaarvoice inline-rating gate for an upsell card.
   *
   * Bazaarvoice is the site's source of truth for ratings, so the upsell reads it
   * directly rather than the numbers Rebuy happens to carry. Rebuy's payload
   * exposes a Yotpo `ratings` object and no usable `reviews` metafields, which is
   * why this card used to disagree with the PDP (HUM-1026).
   *
   * The gate goes in empty; BV's document-wide observer on `[data-bv-show]` fills
   * it in, including for slides injected after page load.
   *
   * @param {any} product
   * @returns {string}
   */
  #buildRatingHtml(product) {
    const productId = product?.id;
    if (!productId) return '';

    return `<div class="cart-bestsellers__rating bv-inline-rating" data-bv-show="inline_rating" data-bv-product-id="${this.#escapeHtml(String(productId))}" data-bv-seo="false"></div>`;
  }

  /**
   * Convert a Rebuy price value to integer cents.
   *
   * Rebuy prices can be either:
   * - Strings like "29.99" (dollars)
   * - Numbers like 2999 (cents)
   *
   * @param {string | number | null | undefined} amount
   * @returns {number} cents, or NaN when unparseable
   */
  #toCents(amount) {
    if (amount == null) return NaN;

    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return NaN;

    // Heuristic: an integer >= 100 is already in cents; otherwise treat as
    // dollars.
    if (Number.isInteger(num) && num >= 100) return num;
    return Math.round(num * 100);
  }

  /**
   * Format a price value to a display string. Uses `Shopify.formatMoney()`
   * when available.
   *
   * @param {string | number | null | undefined} amount
   * @returns {string}
   */
  #formatMoney(amount) {
    const cents = this.#toCents(amount);
    if (isNaN(cents)) return '';

    const shopify = /** @type {any} */ (window).Shopify;
    const theme = /** @type {any} */ (window).Theme;

    if (typeof shopify?.formatMoney === 'function') {
      try {
        return shopify.formatMoney(cents, theme?.money_format);
      } catch {
        // fall through to manual format
      }
    }

    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ─── Add-to-cart (shared by both modes) ─────────────────────────── */

  #handleClick = async (event) => {
    // `.cart-bestsellers__add-btn` = Rebuy cards (built in this file);
    // `.product-card-mini__add-btn` = collection-mode cards (shared Liquid snippet).
    const button = event.target.closest('.cart-bestsellers__add-btn, .product-card-mini__add-btn');
    if (!button) return;

    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled || button.dataset.loading) return;

    event.preventDefault();
    this.#setLoading(button, true);

    try {
      const theme = /** @type {any} */ (window).Theme;
      const addUrl = theme?.routes?.cart_add_url ?? '/cart/add.js';

      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(
          applyPropertiesToPayload({ id: Number(variantId), quantity: 1 }, resolveProperties(button))
        ),
      });

      const addData = await addResponse.json();

      if (addData.status) {
        this.#showError(button, addData.message || 'Could not add to cart.');
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      try {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: this.id || '',
              data: { source: 'cart-bestsellers' },
            },
          })
        );
      } catch {
        // ignore listener errors
      }

      this.#showSuccess(button);
    } catch {
      this.#showError(button, 'Could not add to cart.');
    } finally {
      this.#setLoading(button, false);
    }
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} loading
   */
  #setLoading(button, loading) {
    if (loading) {
      button.dataset.loading = 'true';
      button.disabled = true;
    } else {
      delete button.dataset.loading;
      button.disabled = false;
    }
  }

  /** @param {HTMLButtonElement} button */
  #showSuccess(button) {
    const originalText = button.textContent.trim();
    button.textContent = '✓';
    button.dataset.added = 'true';
    setTimeout(() => {
      button.textContent = originalText;
      delete button.dataset.added;
    }, 2000);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} message
   */
  #showError(button, message) {
    const card = button.closest('.cart-bestsellers__card, .product-card-mini');
    if (!card) return;

    const existing = card.querySelector('.cart-bestsellers__error');
    if (existing) existing.remove();

    const el = document.createElement('span');
    el.className = 'cart-bestsellers__error';
    el.setAttribute('role', 'alert');
    el.textContent = message;
    card.appendChild(el);

    setTimeout(() => el.remove(), 4000);
  }
}

if (!customElements.get('cart-bestsellers-component')) {
  customElements.define('cart-bestsellers-component', CartBestsellersComponent);
}
